import React from 'react';
import {
  ChevronRight,
  Eye,
  Edit,
  Mail,
  RefreshCw,
  Copy,
  Trash2,
  MoreVertical,
  Users,
  Bot
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatCurrency';
import type { ConsumerGroup } from '@/utils/preApprovalGrouping';
import type { PreApprovalListItem, PreApprovalData } from '@/types/pre-approval';
import { RecipientBadgeGroup } from './RecipientBadgeGroup';

interface ConsumerAccordionRowProps {
  group: ConsumerGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onView: (preApproval: PreApprovalListItem) => void;
  onEdit: (preApproval: PreApprovalListItem) => void;
  onSend: (preApproval: PreApprovalListItem, resend: boolean) => void;
  onClone: (preApproval: PreApprovalListItem) => void;
  onDelete: (preApproval: PreApprovalListItem) => void;
  onViewRecipients: (preApproval: PreApprovalListItem) => void;
}

/**
 * Get status badge for a pre-approval status
 */
const getStatusBadge = (status: PreApprovalData['status']) => {
  const statusConfig = {
    draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
    sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700' },
    viewed: { label: 'Viewed', className: 'bg-green-100 text-green-700' },
    accepted: { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700' },
    expired: { label: 'Expired', className: 'bg-red-100 text-red-700' }
  };

  const config = statusConfig[status];
  return <Badge className={config.className}>{config.label}</Badge>;
};

/**
 * Format date as MM/DD/YY and HH:MM AM/PM stacked
 */
const formatDateTime = (dateString?: string) => {
  if (!dateString) {
    return <span className="text-muted-foreground">-</span>;
  }

  const date = new Date(dateString);

  // Format date part: MM/DD/YY
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2);
  const datePart = `${month}/${day}/${year}`;

  // Format time part: HH:MM AM/PM
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const timePart = `${hours}:${minutes} ${ampm}`;

  return (
    <div className="flex flex-col">
      <span className="font-medium">{datePart}</span>
      <span className="text-xs text-muted-foreground">{timePart}</span>
    </div>
  );
};

/**
 * Action dropdown menu for a pre-approval
 */
const PreApprovalActions: React.FC<{
  preApproval: PreApprovalListItem;
  onView: (preApproval: PreApprovalListItem) => void;
  onEdit: (preApproval: PreApprovalListItem) => void;
  onSend: (preApproval: PreApprovalListItem, resend: boolean) => void;
  onClone: (preApproval: PreApprovalListItem) => void;
  onDelete: (preApproval: PreApprovalListItem) => void;
  onViewRecipients: (preApproval: PreApprovalListItem) => void;
}> = ({ preApproval, onView, onEdit, onSend, onClone, onDelete, onViewRecipients }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
        <MoreVertical className="w-4 h-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={(e) => {
 e.stopPropagation(); onView(preApproval); 
}}>
        <Eye className="w-4 h-4 mr-2" />
        View
      </DropdownMenuItem>
      {preApproval.status !== 'draft' && (
        <DropdownMenuItem onClick={(e) => {
 e.stopPropagation(); onViewRecipients(preApproval); 
}}>
          <Users className="w-4 h-4 mr-2" />
          View Recipients
        </DropdownMenuItem>
      )}
      {preApproval.status === 'draft' && (
        <DropdownMenuItem onClick={(e) => {
 e.stopPropagation(); onEdit(preApproval); 
}}>
          <Edit className="w-4 h-4 mr-2" />
          Edit
        </DropdownMenuItem>
      )}
      {preApproval.status === 'draft' && (
        <DropdownMenuItem onClick={(e) => {
 e.stopPropagation(); onSend(preApproval, false); 
}}>
          <Mail className="w-4 h-4 mr-2" />
          Send
        </DropdownMenuItem>
      )}
      {preApproval.status !== 'draft' && (
        <DropdownMenuItem onClick={(e) => {
 e.stopPropagation(); onSend(preApproval, true); 
}}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Resend
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={(e) => {
 e.stopPropagation(); onClone(preApproval); 
}}>
        <Copy className="w-4 h-4 mr-2" />
        Clone
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={(e) => {
 e.stopPropagation(); onDelete(preApproval); 
}}
        className="text-red-600"
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

/**
 * Consumer Accordion Row Component
 * Displays a consumer group with expandable history of pre-approvals
 */
export const ConsumerAccordionRow: React.FC<ConsumerAccordionRowProps> = ({
  group,
  isExpanded,
  onToggle,
  onView,
  onEdit,
  onSend,
  onClone,
  onDelete,
  onViewRecipients
}) => {
  const { latestPreApproval, preApprovals, totalCount, activeCount, expiredCount } = group;

  // Only show expand capability if there are multiple pre-approvals
  const hasHistory = totalCount > 1;

  return (
    <>
      {/* Parent Row - Always visible */}
      <TableRow
        className={cn(
          "transition-colors",
          hasHistory && "cursor-pointer hover:bg-muted/50",
          isExpanded && "bg-muted/30"
        )}
        onClick={hasHistory ? onToggle : undefined}
      >
        {/* Consumer Name + Email */}
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            {hasHistory && (
              <ChevronRight
                className={cn(
                  "w-4 h-4 transition-transform text-muted-foreground",
                  isExpanded && "rotate-90"
                )}
              />
            )}
            {!hasHistory && <div className="w-4" />}
            <div>
              <div className="flex items-center gap-2">
                <span>{group.consumerName}</span>
                {latestPreApproval.sentByWisr && (
                  <Badge variant="outline" className="gap-1 text-xs px-1.5 py-0 text-purple-600 border-purple-300 bg-purple-50">
                    <Bot className="w-3 h-3" />
                    Wisr
                  </Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground">{group.consumerEmail}</div>
            </div>
          </div>
        </TableCell>

        {/* Latest Amount */}
        <TableCell>{formatCurrency(latestPreApproval.preApprovalAmount)}</TableCell>

        {/* Latest Created Date */}
        <TableCell>{formatDateTime(latestPreApproval.createdAt)}</TableCell>

        {/* Latest Sent Date */}
        <TableCell>{formatDateTime(latestPreApproval.sentAt)}</TableCell>

        {/* Recipients for latest */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          <RecipientBadgeGroup
            recipientsSummary={latestPreApproval.recipientsSummary}
            onBadgeClick={() => onViewRecipients(latestPreApproval)}
          />
        </TableCell>

        {/* History Count */}
        <TableCell>
          {hasHistory ? (
            <div className="flex flex-col gap-0.5">
              <Badge variant="outline" className="w-fit">
                {totalCount} total
              </Badge>
              {expiredCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  {activeCount} active, {expiredCount} expired
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>

        {/* Actions */}
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
          <PreApprovalActions
            preApproval={latestPreApproval}
            onView={onView}
            onEdit={onEdit}
            onSend={onSend}
            onClone={onClone}
            onDelete={onDelete}
            onViewRecipients={onViewRecipients}
          />
        </TableCell>
      </TableRow>

      {/* Expanded History Rows */}
      {isExpanded && hasHistory && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={7} className="p-0 bg-muted/20 border-t">
            <div className="px-4 py-3 pl-12">
              <div className="text-sm font-medium text-muted-foreground mb-2">
                Pre-Approval History
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-8 text-xs">Amount</TableHead>
                    <TableHead className="h-8 text-xs">Created</TableHead>
                    <TableHead className="h-8 text-xs">Sent</TableHead>
                    <TableHead className="h-8 text-xs">Status</TableHead>
                    <TableHead className="h-8 text-xs">Recipients</TableHead>
                    <TableHead className="h-8 text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preApprovals.map((pa, index) => (
                    <TableRow
                      key={pa.id}
                      className={cn(
                        "hover:bg-muted/30",
                        index === 0 && "bg-primary/5" // Highlight latest
                      )}
                    >
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          {formatCurrency(pa.preApprovalAmount)}
                          {index === 0 && (
                            <Badge variant="outline" className="text-xs px-1 py-0">
                              Latest
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">{formatDateTime(pa.createdAt)}</TableCell>
                      <TableCell className="py-2">{formatDateTime(pa.sentAt)}</TableCell>
                      <TableCell className="py-2">
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(pa.status)}
                          {pa.expiredReason === 'superseded_by_new' && (
                            <span className="text-xs text-muted-foreground">Superseded</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2">
                        <RecipientBadgeGroup
                          recipientsSummary={pa.recipientsSummary}
                          onBadgeClick={() => onViewRecipients(pa)}
                        />
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <PreApprovalActions
                          preApproval={pa}
                          onView={onView}
                          onEdit={onEdit}
                          onSend={onSend}
                          onClone={onClone}
                          onDelete={onDelete}
                          onViewRecipients={onViewRecipients}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ConsumerAccordionRow;
